import type {
  Cadence,
  ReportEditorDto,
} from '@act/shared/data-transfer-objects';
import {
  formatCadenceLabel,
  matchesDefaultReportName,
} from '@act/shared/data-transfer-objects';
import { formatReportTimestamp } from './reportDisplay.util';

export interface BuildReportReadingRouteHeaderParams {
  reportName: string;
  customerName: string;
  measurementPeriodLabel: string;
  cadence: Cadence;
  includedSectionCount: number;
  updatedAt: string;
  lastEditedBy?: ReportEditorDto | null;
}

export interface ReportReadingRouteHeader {
  title: string;
  subtitle: string;
}

/**
 * Builds view/print route titles that avoid repeating default report names.
 * Default names surface customer + operational metadata; custom names keep context in the subtitle.
 * @param params - Report identity, cadence, section count, and freshness fields.
 * @returns Title and subtitle strings for reading-route toolbars.
 */
export const buildReportReadingRouteHeader = (
  params: BuildReportReadingRouteHeaderParams,
): ReportReadingRouteHeader => {
  const isDefaultName = matchesDefaultReportName({
    name: params.reportName,
    customerName: params.customerName,
    measurementPeriodLabel: params.measurementPeriodLabel,
  });

  if (isDefaultName) {
    const subtitleParts = [
      params.measurementPeriodLabel,
      formatCadenceLabel(params.cadence),
      `${params.includedSectionCount} section${params.includedSectionCount === 1 ? '' : 's'}`,
      buildUpdatedSubtitle(params.updatedAt, params.lastEditedBy),
    ].filter(part => part.length > 0);

    return {
      title: params.customerName,
      subtitle: subtitleParts.join(' · '),
    };
  }

  const subtitleParts = [
    params.customerName,
    params.measurementPeriodLabel,
  ].filter(part => part.length > 0);

  return {
    title: params.reportName,
    subtitle: subtitleParts.join(' · '),
  };
};

const buildUpdatedSubtitle = (
  updatedAt: string,
  lastEditedBy?: ReportEditorDto | null,
): string => {
  const updatedLabel = formatReportTimestamp(updatedAt);
  if (lastEditedBy?.name?.trim()) {
    return `Updated ${updatedLabel} by ${lastEditedBy.name.trim()}`;
  }
  return `Updated ${updatedLabel}`;
};
